import {
  format,
  parseISO,
  differenceInSeconds,
  differenceInMinutes,
  addMinutes,
} from 'date-fns';

type AllowedDates = string | Date | number;

/**
 * For naming CSVs we can get file friendly date name (like 2021-12-20)
 * @param date
 */
// export const formatFileFriendlyDateDay = (date?: AllowedDates): string => {
//   if (!date) {
//     return '-';
//   }
//   try {
//     return format(
//       typeof date === 'string' ? parseISO(date) : date,
//       'yyyy-MM-dd'
//     );
//   } catch (err) {
//     console.warn('Date formatting problem', err);
//     return '-';
//   }
// };

export const formatDateTime = (
  date?: AllowedDates,
  opts?: { /** Default true */ hours?: boolean; seconds?: boolean }
): string => {
  if (!date) {
    return '-';
  }
  try {
    return format(
      typeof date === 'string' ? parseISO(date) : date,
      `yyyy-MM-dd${opts?.hours !== false ? ' HH:mm' : ''}${
        opts?.seconds ? ':ss' : ''
      }`
    );
  } catch (err) {
    console.warn('Date formatting problem', err);
    return '-';
  }
};

export const formatTime = (date?: AllowedDates): string => {
  if (!date) {
    return '-';
  }
  try {
    return format(typeof date === 'string' ? parseISO(date) : date, 'HH:mm:ss');
  } catch (err) {
    console.warn(err);
    return '-';
  }
};

export const formatDateWithMonthAsWord = (
  date?: AllowedDates,
  formatIn?: 'utc'
): string | null => {
  if (!date) {
    return null;
  }
  try {
    const dateObject =
      typeof date === 'number'
        ? new Date(date)
        : typeof date === 'string'
        ? parseISO(date)
        : date;
    const dateObjectUpdated =
      formatIn === 'utc'
        ? addMinutes(dateObject, dateObject.getTimezoneOffset())
        : dateObject;
    return format(dateObjectUpdated, 'MMM dd');
  } catch (err) {
    console.warn('Date formatting problem', err);
    return null;
  }
};

export const formatDateWithFullMonthAsWord = (
  date?: AllowedDates,
  opts: {
    utc?: boolean;
    includeYear?: boolean;
  } = {}
): string | null => {
  if (!date) {
    return null;
  }
  try {
    const dateObject =
      typeof date === 'number'
        ? new Date(date)
        : typeof date === 'string'
        ? parseISO(date)
        : date;
    const dateObjectUpdated = opts.utc
      ? addMinutes(dateObject, dateObject.getTimezoneOffset())
      : dateObject;
    const formatStr = opts.includeYear
      ? 'MMMM dd yyyy hh:mm a'
      : 'MMMM dd hh:mm a';
    return format(dateObjectUpdated, formatStr);
  } catch (err) {
    console.warn('Date formatting problem', err);
    return null;
  }
};

// export const formatDateWithFullMonthAsWordWithYear = (
//   date?: AllowedDates,
//   formatIn?: 'utc'
// ): string | null => {
//   if (!date) {
//     return null;
//   }
//   try {
//     const dateObject =
//       typeof date === 'number'
//         ? new Date(date)
//         : typeof date === 'string'
//         ? parseISO(date)
//         : date;
//     const dateObjectUpdated =
//       formatIn === 'utc'
//         ? addMinutes(dateObject, dateObject.getTimezoneOffset())
//         : dateObject;
//     return format(dateObjectUpdated, 'MMMM dd hh:mm a');
//   } catch (err) {
//     console.warn('Date formatting problem', err);
//     return null;
//   }
// };

export type RemainingTime = {
  ended: boolean;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

export const remainingDaysHoursMinutesSeconds = (
  endDateObject: Date
): RemainingTime => {
  const diffSecs = differenceInSeconds(endDateObject, new Date());
  return durationDaysHoursMinutesSeconds(diffSecs * 1000);
};

/**
 * Splits a duration in milliseconds into days/hours/minutes/seconds.
 * Unlike remainingDaysHoursMinutesSeconds, it does not compare against "now".
 */
export const durationDaysHoursMinutesSeconds = (
  durationMs: number
): RemainingTime => {
  const diffSecs = Math.floor(durationMs / 1000);
  if (diffSecs <= 0) {
    return { ended: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  const days = Math.floor(diffSecs / 60 / 60 / 24);
  const daysInSec = days * 24 * 60 * 60;

  const hours = Math.floor((diffSecs - daysInSec) / 60 / 60);
  const hoursInSec = hours * 60 * 60;

  const minutes = Math.floor((diffSecs - daysInSec - hoursInSec) / 60);
  const minutesInSec = minutes * 60;

  const seconds = diffSecs - daysInSec - hoursInSec - minutesInSec;
  return {
    ended: false,
    days,
    hours,
    minutes,
    seconds,
  };
};

/**
 * Duration in seconds as the period of a rate - "year", "day" or "30 days",
 * eg. a fee quoted as "1% / year".
 */
export const formatDurationAsPeriod = (durationInS: number): string => {
  const days = durationInS / 86400;
  if (days === 365) {
    return 'year';
  }
  return days === 1 ? 'day' : `${Number(days.toFixed(2))} days`;
};

/**
 * Check whenever user is of in allowed age range
 */
// export const isValidPersonAge = (
//   dateOfBirth: string,
//   minAge: number,
//   maxAge: number
// ): false | 'valid' | 'incorrect' => {
//   const dateOfBirthObj = new Date(dateOfBirth);
//   const birthYear = dateOfBirthObj.getFullYear();
//   const birthMonth = dateOfBirthObj.getMonth();
//   const birthDay = dateOfBirthObj.getDate();

//   const currentDate = new Date();
//   const currentYear = currentDate.getFullYear();
//   const currentMonth = currentDate.getMonth();
//   const currentDay = currentDate.getDate();

//   const userAgeThisYear = currentYear - birthYear;
//   if (userAgeThisYear < minAge) {
//     return false;
//   }
//   if (userAgeThisYear > maxAge) {
//     return 'incorrect';
//   }

//   if (userAgeThisYear === minAge) {
//     if (birthMonth < currentMonth) {
//       return 'valid';
//     }
//     if (birthMonth > currentMonth) {
//       return false;
//     }
//     return !(birthMonth === currentMonth && birthDay > currentDay)
//       ? 'valid'
//       : false;
//   }
//   return 'valid';
// };

export const hoursDifferenceLongerThan = (
  ts1: number | Date,
  ts2: number | Date,
  hours: number
): boolean => {
  return Math.abs(differenceInMinutes(ts1, ts2) / 60) > hours;
};

// export const guessUserTimezone = (): string | undefined => {
//   if (window?.Intl && window.Intl.DateTimeFormat) {
//     return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
//   }
//   console.warn('No Intl support - cannot detect user timezone');
//   return undefined;
// };
